/**
 * One plugin's release, from publish through to impact.
 *
 * Four stages, because a plugin release is four separable things that fail
 * differently: the artifact reaches the catalog, it installs into the plugin
 * site, the running process rolls onto it, and tasks either keep working or
 * do not. Collapsing them into "installed / not installed" is what made a
 * regression like a bad dependency pin take four screens to diagnose.
 *
 * All four stages read real data. Roll-forward does since Feature 40: `roll_forward.py`
 * implements the debounce and drain, but the only exposed surface is
 * `POST /plugins/roll-now`, so phase, deadline and in-flight count cannot be
 * read. That stage says so and offers the action that does exist, rather than
 * animating a countdown against a deadline nobody told us.
 */
import { getCatalogEntry, installCatalogEntry } from '@optum/api-client/endpoints/catalog';
import { listEnvironmentJobs } from '@optum/api-client/endpoints/environment';
import { getRollStatus, rollPluginsNow } from '@optum/api-client/endpoints/plugins';
import { listOrganizations } from '@optum/api-client/endpoints/organizations';
import { listTasks } from '@optum/api-client/endpoints/tasks';
import type {
  CatalogEntryOut,
  CatalogKind,
  EnvironmentJobOut,
  InstallJobOut,
  RollStatus,
  Task,
} from '@optum/api-client/types';
import { ApiError } from '@optum/api-client/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { useWindowStart } from '../../lib/use-time-window.js';
import { Band } from '../../ui/Band.js';
import { RecordShape, ShapeError, ShapeLoading } from '../../ui/shapes.js';
import { Badge, Icon, Typography, type BadgeTone } from '../../ui/finixt.js';
import { environmentJobLines } from './environment-job-lines.js';

const HOUR_MS = 60 * 60 * 1000;

type StageState = 'complete' | 'active' | 'failing' | 'pending' | 'unknown';

const STATE_COLOR: Record<StageState, BadgeTone> = {
  complete: 'ok',
  active: 'warn',
  failing: 'danger',
  pending: 'neutral',
  unknown: 'neutral',
};

// Static, never interpolated: Tailwind scans source text, so a class built
// from a template literal is invisible to it and renders unstyled at runtime
// with nothing failing at build time.
const STATE_TILE: Record<StageState, CSSProperties> = {
  complete: { background: 'var(--ok-tint)', color: 'var(--ok)' },
  active: { background: 'var(--warn-tint)', color: 'var(--warn)' },
  failing: { background: 'var(--danger-tint)', color: 'var(--danger)' },
  pending: { background: 'var(--surface-fill)', color: 'var(--text-muted)' },
  unknown: { background: 'var(--surface-fill)', color: 'var(--text-muted)' },
};

const STATE_BAR: Record<StageState, string> = {
  complete: 'var(--ok)',
  active: 'var(--warn)',
  failing: 'var(--danger)',
  pending: 'var(--border-hairline)',
  unknown: 'var(--border-hairline)',
};

const STATE_ICON: Record<StageState, string> = {
  complete: 'check',
  active: 'schedule',
  failing: 'warn',
  pending: 'more_horiz',
  unknown: 'help',
};

export interface LogLine {
  text: string;
  meta: string;
  tone: 'ok' | 'warn' | 'danger' | 'info' | 'neutral';
}

interface Stage {
  n: number;
  title: string;
  state: StageState;
  meta: string;
  description: string;
  log: LogLine[];
}

const DOT: Record<LogLine['tone'], string> = {
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
  info: 'var(--info)',
  neutral: 'var(--grey-500)',
};

export function PluginReleasePage() {
  const { kind = 'capabilities', name = '' } = useParams<{ kind: CatalogKind; name: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<number | null>(null);

  const entry = useQuery({
    queryKey: qk.catalogEntry(kind as CatalogKind, name),
    queryFn: () => getCatalogEntry(kind as CatalogKind, name),
  });
  const orgs = useQuery({ queryKey: qk.orgs(), queryFn: () => listOrganizations() });
  // Tools have no environment of their own, so no jobs to show.
  const environmentJobs = useQuery({
    queryKey: qk.pluginEnvironmentJobs(name),
    queryFn: () => listEnvironmentJobs(name),
    enabled: kind !== 'tools',
  });
  const since = useWindowStart(2 * HOUR_MS);
  const tasks = useQuery({
    queryKey: qk.tasks({ capability_id: name, created_after: since }),
    queryFn: () => listTasks({ capability_id: name, created_after: since, limit: 500 }),
    // Only capabilities produce tasks; a tool or policy has no task stream.
    enabled: kind === 'capabilities',
  });
  // The window is a countdown, so a stale figure is worse than none. Short
  // interval, no cache: this is the one number on the page that moves on its
  // own while an operator is reading it.
  const rollStatus = useQuery({
    queryKey: qk.rollStatus(),
    queryFn: getRollStatus,
    refetchInterval: 15_000,
    staleTime: 0,
  });

  /**
   * Roll back = install the previous version. There is no rollback endpoint;
   * the catalog is version-addressed, so re-installing the last good release
   * IS the rollback, and it reuses the install path the forward direction
   * already exercises.
   */
  const previous = entry.data ? previousVersion(entry.data) : null;
  const rollback = useMutation({
    mutationFn: (version: string) => installCatalogEntry({ name, version }),
    onSuccess: (job) => {
      toast.success(
        `Rolling back to v${job.version}`,
        'Agents paused by the failing version resume once it serves.',
      );
      qc.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: (e) => toast.error('Roll back failed', e instanceof ApiError ? e.message : String(e)),
  });

  const roll = useMutation({
    mutationFn: rollPluginsNow,
    onSuccess: () => {
      // Not "drains in-flight tasks": uvicorn drains in-flight HTTP requests
      // and the process ends. Executing tasks are interrupted, which is why
      // the stage shows how many there are before you press this.
      toast.success('Roll-forward requested', 'The process restarts onto the installed version.');
      qc.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: (e) => toast.error('Roll failed', e instanceof ApiError ? e.message : String(e)),
  });

  if (entry.isPending) {
    return <ShapeLoading shape="record" />;
  }
  if (entry.isError) {
    return <ShapeError shape="record" error={entry.error} fallback="Failed to load the plugin" />;
  }

  const stages = buildStages(
    entry.data,
    tasks.data ?? [],
    tasks.isPending,
    rollStatus.data,
    environmentJobs.data ?? [],
  );
  const current = selected ?? activeStage(stages);
  const detail = stages[current - 1] ?? stages[0]!;
  const impact = summariseImpact(tasks.data ?? []);

  return (
    <RecordShape
      back={{ to: `/plugins/${kind}/${encodeURIComponent(name)}`, label: name }}
      eyebrow={
        <Typography variant="eyebrow">
          {`Release · v${entry.data.installed_version ?? entry.data.latest_version}`}
        </Typography>
      }
      title={name}
      subtitle="Publish through to impact, with the drain window made visible."
    >
      <div style={{ marginTop: 22, display: 'flex', flexDirection: 'row', gap: 0 }}>
        {stages.map((stage) => (
          <button
            key={stage.n}
            type="button"
            onClick={() => setSelected(stage.n)}
            style={{
              display: 'flex',
              flex: '1 1 0%',
              flexDirection: 'column',
              gap: 11,
              borderRadius: 'var(--radius-card)',
              padding: 16,
              textAlign: 'left',
              cursor: 'pointer',
              border: `1px solid ${stage.n === current ? 'var(--border-input)' : 'transparent'}`,
              background: stage.n === current ? 'var(--surface-fill)' : 'transparent',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <span
                style={{
                  display: 'grid',
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  placeItems: 'center',
                  borderRadius: 'var(--radius-pill)',
                  ...STATE_TILE[stage.state],
                }}
              >
                <Icon name={STATE_ICON[stage.state]} size={14} />
              </span>
              <span style={{ minWidth: 0 }}>
                <Typography variant="eyebrow" size="xs" style={{ display: 'block' }}>
                  Stage {stage.n}
                </Typography>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-body-md)',
                    fontWeight: 'var(--weight-semibold)',
                    color: 'var(--text-strong)',
                  }}
                >
                  {stage.title}
                </span>
              </span>
            </span>
            <span
              style={{
                height: 4,
                borderRadius: 'var(--radius-pill)',
                background: STATE_BAR[stage.state],
              }}
            />
            <span
              className="mono"
              style={{
                fontSize: 'var(--text-meta-lg)',
                color:
                  stage.state === 'failing'
                    ? 'var(--danger)'
                    : stage.state === 'active'
                      ? 'var(--warn)'
                      : 'var(--text-muted)',
              }}
            >
              {stage.meta}
            </span>
          </button>
        ))}
      </div>

      <div
        style={{
          marginTop: 18,
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,0.7fr)',
          alignItems: 'flex-start',
          gap: 16,
        }}
      >
        <div
          style={{
            overflow: 'hidden',
            borderRadius: 'var(--radius-card)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-card)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              borderBottom: '1px solid var(--border-hairline)',
              borderColor: 'var(--border-hairline)',
              background: 'var(--surface-sunken)',
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 11,
              paddingBottom: 11,
            }}
          >
            <Typography variant="eyebrow" style={{ fontWeight: 'var(--weight-bold)' }}>
              {detail.title}
            </Typography>
            <Badge tone={STATE_COLOR[detail.state]}>{detail.state}</Badge>
          </div>
          <div style={{ padding: 16 }}>
            <p
              style={{
                marginBottom: 16,
                fontSize: 'var(--text-body-md)',
                lineHeight: 1.625,
                color: 'var(--text-soft)',
              }}
            >
              {detail.description}
            </p>
            <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column' }}>
              {detail.log.map((line) => (
                <div
                  key={line.text}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 11,
                    paddingTop: 7,
                    paddingBottom: 7,
                  }}
                >
                  <span
                    style={{
                      marginTop: 7,
                      width: 8,
                      height: 8,
                      flexShrink: 0,
                      borderRadius: 'var(--radius-pill)',
                      background: DOT[line.tone],
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--text-body-md)',
                        color: 'var(--text-body)',
                      }}
                    >
                      {line.text}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--text-meta-lg)',
                        color: 'var(--text-muted)',
                      }}
                      className="mono"
                    >
                      {line.meta}
                    </span>
                  </span>
                </div>
              ))}
              {detail.log.length === 0 && (
                <p
                  style={{
                    paddingTop: 7,
                    paddingBottom: 7,
                    fontSize: 'var(--text-meta-lg)',
                    color: 'var(--text-muted)',
                  }}
                >
                  Nothing recorded for this stage.
                </p>
              )}
            </div>
            {detail.n === 3 && (
              <Button
                variant="primary"
                icon="restart_alt"
                onClick={() => roll.mutate()}
                disabled={roll.isPending}
              >
                {roll.isPending ? 'Requesting…' : 'Roll forward now'}
              </Button>
            )}
            {detail.n === 4 && detail.state === 'failing' && entry.data?.is_infra && (
              // The platform reinstalls an infra plugin from its channel at
              // every boot (Feature 84): an older install would not last.
              <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                An infra plugin follows its channel: publish a fixed version to roll back.
              </span>
            )}
            {detail.n === 4 && detail.state === 'failing' && !entry.data?.is_infra && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 11 }}>
                <Button
                  variant="danger"
                  icon="undo"
                  onClick={() => previous && rollback.mutate(previous)}
                  disabled={!previous || rollback.isPending}
                >
                  {rollback.isPending
                    ? 'Rolling back…'
                    : previous
                      ? `Roll back to v${previous}`
                      : 'No earlier version to roll back to'}
                </Button>
                {previous && (
                  <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                    Re-installs v{previous}; the next roll-forward serves it.
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <Band label="Affected tenants" style={{ marginBottom: 11 }} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border-hairline)',
              background: 'var(--surface-card)',
              padding: 16,
            }}
          >
            {impact.length === 0 && (
              <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                {kind === 'capabilities'
                  ? 'No tasks ran this capability in the last two hours.'
                  : 'Only capabilities produce tasks.'}
              </p>
            )}
            {impact.map((row) => {
              const org = (orgs.data ?? []).find((o) => o.id === row.organizationId);
              return (
                <div
                  key={row.organizationId}
                  style={{ display: 'flex', alignItems: 'center', gap: 7 }}
                >
                  <span
                    style={{
                      minWidth: 0,
                      flex: '1 1 0%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 'var(--text-meta-lg)',
                      color: 'var(--text-soft)',
                    }}
                  >
                    {org?.name ?? row.organizationId}
                  </span>
                  <Badge tone={row.failed > 0 ? 'danger' : 'ok'}>
                    {row.failed > 0 ? `${row.failed} failing` : 'healthy'}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </RecordShape>
  );
}

/**
 * The newest non-yanked version below the one installed.
 *
 * Compared numerically per segment, so v1.10.0 sorts above v1.9.0 — a string
 * compare puts them the other way round and would offer a roll back to a
 * version newer than the one that broke.
 */
function previousVersion(entry: CatalogEntryOut): string | null {
  const installed = entry.installed_version;
  if (!installed) return null;
  const older = entry.versions
    .filter((v) => !v.yanked && compareVersions(v.version, installed) < 0)
    .sort((a, b) => compareVersions(b.version, a.version));
  return older[0]?.version ?? null;
}

export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

// ── Stage derivation ──────────────────────────────────────────────────

/** The first stage that is not complete — what the operator should look at. */
function activeStage(stages: Stage[]): number {
  const attention =
    stages.find((s) => s.state === 'failing') ?? stages.find((s) => s.state !== 'complete');
  return attention?.n ?? stages.length;
}

function buildStages(
  entry: CatalogEntryOut,
  tasks: Task[],
  tasksLoading: boolean,
  roll: RollStatus | undefined,
  environmentJobs: EnvironmentJobOut[],
): Stage[] {
  const installed = entry.installed_version;
  const published = entry.versions.find((v) => v.version === entry.latest_version);
  const job = entry.latest_job;

  return [
    publishStage(entry, published),
    installStage(job, installed, entry, environmentJobs),
    rollForwardStage(entry, roll),
    verifyStage(tasks, tasksLoading),
  ];
}

function publishStage(
  entry: CatalogEntryOut,
  published: CatalogEntryOut['versions'][number] | undefined,
): Stage {
  return {
    n: 1,
    title: 'Publish',
    state: published ? 'complete' : 'pending',
    meta: published ? `sdist · ${entry.versions.length} versions` : 'nothing published',
    description:
      'CI uploaded the source distribution to the private catalog. Publishing does not install anything.',
    log: published
      ? [
          {
            text: `${entry.name}-${published.version}.tar.gz published`,
            meta: absoluteDateTime(published.published_at),
            tone: 'ok',
          },
          {
            text: 'sha256 recorded',
            meta: published.sdist_sha256.slice(0, 24) + '…',
            tone: 'ok',
          },
        ]
      : [],
  };
}

function installStage(
  job: InstallJobOut | null,
  installed: string | null,
  entry: CatalogEntryOut,
  environmentJobs: EnvironmentJobOut[],
): Stage {
  // Environment jobs rebuild the installed version in place; they sit in the
  // Install stage's log, in time order after the install that they modify.
  const environmentLines = environmentJobLines(environmentJobs);
  if (!job) {
    return {
      n: 2,
      title: 'Install',
      state: installed ? 'complete' : 'pending',
      meta: installed ? `v${installed} in the plugin site` : 'not installed',
      description:
        'The wheel and its tool dependencies are installed into the plugin site. The running process still serves the previous version until it rolls.',
      log: environmentLines,
    };
  }

  const duration =
    job.finished_at && job.started_at
      ? `${Math.max(0, Math.round((Date.parse(job.finished_at) - Date.parse(job.started_at)) / 1000))}s`
      : 'running';
  const state: StageState =
    job.state === 'failed' ? 'failing' : job.state === 'installed' ? 'complete' : 'active';

  const log: LogLine[] = [
    {
      text: `v${job.version} — ${job.state}`,
      meta: `${duration} · requested by ${job.requested_by}`,
      tone: state === 'failing' ? 'danger' : state === 'complete' ? 'ok' : 'info',
    },
  ];
  // What the install put on disk, read back from the entry's environment
  // rather than predicted from a catalog walk.
  const tools = (entry.environment ?? []).filter((e) => e.optum && e.name !== entry.name);
  if (tools.length > 0) {
    log.push({
      text: `Resolved ${tools.length} tool dependenc${tools.length === 1 ? 'y' : 'ies'}`,
      meta: tools.map((t) => `${t.name} ${t.version}`).join(', '),
      tone: 'ok',
    });
  }
  if (job.error) {
    log.push({ text: job.error, meta: 'install error', tone: 'danger' });
  }
  log.push(...environmentLines);

  return {
    n: 2,
    title: 'Install',
    state,
    meta: `pip --target · ${duration}`,
    description:
      'The wheel and its tool dependencies are installed into the plugin site. The running process still serves the previous version until it rolls.',
    log,
  };
}

/**
 * The one stage with no data behind it.
 *
 * `installed_version` vs `latest_version` says whether a roll is outstanding,
 * which is real. Phase, drain deadline and in-flight count are not readable —
 * that is backend gap 3 — so this says what is missing rather than inventing a
 * countdown, which would be the most convincing wrong thing on the page.
 */
function rollForwardStage(entry: CatalogEntryOut, roll: RollStatus | undefined): Stage {
  const pending = entry.upgrade_available;
  const log: Stage['log'] = [
    {
      text: pending
        ? `Installed v${entry.installed_version ?? '—'}, published v${entry.latest_version}`
        : 'Installed version matches the catalog',
      meta: pending ? 'a roll is outstanding' : 'nothing to roll',
      tone: pending ? 'warn' : 'ok',
    },
  ];

  if (!roll) {
    log.push({ text: 'Reading roll-forward state…', meta: '', tone: 'neutral' });
  } else if (!roll.enabled) {
    // Development leaves the exit hook unwired on purpose, so an armed timer
    // there would never fire. Saying "no pending roll" would be a lie.
    log.push({
      text: 'Roll-forward is not wired in this environment',
      meta: 'no supervisor would restart the process',
      tone: 'neutral',
    });
  } else if (roll.armed) {
    log.push({
      text: `Restarts in ${formatDuration(roll.seconds_until_roll ?? 0)}`,
      meta: `debounce window ${formatDuration(roll.debounce_seconds)}`,
      tone: 'warn',
    });
  } else {
    log.push({
      text: 'No restart pending',
      meta: 'the window is armed by an install',
      tone: 'ok',
    });
  }

  if (roll?.enabled) {
    log.push({
      text: `${roll.tasks_in_flight} of ${roll.task_capacity} task slots busy`,
      meta:
        roll.tasks_in_flight > 0 ? 'rolling now interrupts them' : 'rolling now interrupts nothing',
      tone: roll.tasks_in_flight > 0 ? 'warn' : 'ok',
    });
  }

  return {
    n: 3,
    title: 'Roll-forward',
    state: pending ? 'active' : roll?.armed ? 'active' : 'unknown',
    meta: rollMeta(entry, roll),
    // Only in-flight HTTP requests drain. Tasks executing in the process are
    // interrupted, which is why their count is on the stage.
    description:
      'Installing does not activate. Once the debounce window closes the process restarts onto the installed version; uvicorn drains in-flight requests on the way out.',
    log,
  };
}

function rollMeta(entry: CatalogEntryOut, roll: RollStatus | undefined): string {
  if (roll?.enabled && roll.armed) {
    return `restarts in ${formatDuration(roll.seconds_until_roll ?? 0)}`;
  }
  if (entry.upgrade_available) return `v${entry.latest_version} not yet serving`;
  return 'no pending roll';
}

/** Whole units only — a countdown to the second implies a precision the poll interval does not have. */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/**
 * Post-roll outcomes, approximated.
 *
 * The design compares against "the hour before the roll"; we have no restart
 * timestamp, so this compares the last hour with the one before it. Named
 * honestly in the meta line, because a rate that looks like a regression
 * signal but is measured from an arbitrary point is worse than no signal.
 */
function verifyStage(tasks: Task[], loading: boolean): Stage {
  if (loading) {
    return {
      n: 4,
      title: 'Verify',
      state: 'unknown',
      meta: 'loading…',
      description: 'Recent outcomes for this capability.',
      log: [],
    };
  }

  const cutoff = Date.now() - HOUR_MS;
  const recent = tasks.filter((t) => Date.parse(t.created_at) >= cutoff);
  const prior = tasks.filter((t) => Date.parse(t.created_at) < cutoff);
  const failed = recent.filter((t) => t.status === 'failed').length;
  const priorFailed = prior.filter((t) => t.status === 'failed').length;

  const state: StageState = failed > 0 ? 'failing' : recent.length > 0 ? 'complete' : 'unknown';
  const reasons = new Map<string, number>();
  for (const t of recent) {
    if (t.status === 'failed' && t.failure_reason) {
      reasons.set(t.failure_reason, (reasons.get(t.failure_reason) ?? 0) + 1);
    }
  }
  const top = [...reasons.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2);

  return {
    n: 4,
    title: 'Verify',
    state,
    meta:
      recent.length === 0
        ? 'no tasks in the last hour'
        : failed > 0
          ? `${failed} failures in the last hour`
          : `${recent.length} tasks, none failed`,
    description:
      'Outcomes since the most recent hour, against the hour before it. There is no restart timestamp to measure from, so this is a recent-trend signal rather than a before-and-after of the roll itself.',
    log: [
      {
        text: `Last hour: ${failed} of ${recent.length} failed`,
        meta: `previous hour: ${priorFailed} of ${prior.length}`,
        tone: failed > priorFailed ? 'danger' : failed > 0 ? 'warn' : 'ok',
      },
      ...top.map(
        ([reason, n]): LogLine => ({
          text: reason.length > 120 ? `${reason.slice(0, 117)}…` : reason,
          meta: `${n} task${n === 1 ? '' : 's'}`,
          tone: 'danger',
        }),
      ),
    ],
  };
}

function summariseImpact(tasks: Task[]): { organizationId: string; failed: number }[] {
  const byOrg = new Map<string, number>();
  for (const t of tasks) {
    const failed = byOrg.get(t.organization_id) ?? 0;
    byOrg.set(t.organization_id, failed + (t.status === 'failed' ? 1 : 0));
  }
  return [...byOrg.entries()]
    .map(([organizationId, failed]) => ({ organizationId, failed }))
    .sort((a, b) => b.failed - a.failed)
    .slice(0, 8);
}
